import React, { useEffect } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

/**
 * Reusable Notification/Toast component
 * Replaces window.alert() with styled notification toasts
 */
const Notification = ({
  isOpen,
  onClose,
  title,
  message,
  variant = 'info', // 'success', 'error', 'warning', 'info'
  duration = 5000, // Auto-close after 5 seconds (0 = no auto-close)
  position = 'top-right' // 'top-right', 'top-center', 'bottom-right', 'bottom-center'
}) => {
  useEffect(() => {
    if (isOpen && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [isOpen, duration, onClose]);

  if (!isOpen) return null;

  const variants = {
    success: {
      icon: CheckCircle,
      bgColor: 'bg-green-50',
      borderColor: 'border-green-500',
      iconColor: 'text-green-600',
      titleColor: 'text-green-900',
      textColor: 'text-green-700'
    },
    error: {
      icon: XCircle,
      bgColor: 'bg-red-50',
      borderColor: 'border-red-500',
      iconColor: 'text-red-600',
      titleColor: 'text-red-900',
      textColor: 'text-red-700'
    },
    warning: {
      icon: AlertTriangle,
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-500',
      iconColor: 'text-yellow-600',
      titleColor: 'text-yellow-900',
      textColor: 'text-yellow-700'
    },
    info: {
      icon: Info,
      bgColor: 'bg-gray-50',
      borderColor: 'border-yellow-500',
      iconColor: 'text-yellow-600',
      titleColor: 'text-gray-900',
      textColor: 'text-yellow-600'
    }
  };

  const positions = {
    'top-right': 'top-4 right-4',
    'top-center': 'top-4 left-1/2 transform -translate-x-1/2',
    'bottom-right': 'bottom-4 right-4',
    'bottom-center': 'bottom-4 left-1/2 transform -translate-x-1/2'
  };

  const config = variants[variant] || variants.info;
  const Icon = config.icon;
  const positionClass = positions[position] || positions['top-right'];

  return (
    <div className={`fixed ${positionClass} z-50 animate-slide-in`}>
      <div
        className={`${config.bgColor} ${config.borderColor} border-l-4 rounded-lg shadow-lg p-4 max-w-md w-full`}
      >
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <Icon className={`h-5 w-5 ${config.iconColor}`} />
          </div>
          <div className="ml-3 flex-1">
            {title && (
              <h3 className={`text-sm font-medium ${config.titleColor} mb-1`}>
                {title}
              </h3>
            )}
            <p className={`text-sm ${config.textColor}`}>
              {message}
            </p>
          </div>
          <button
            onClick={onClose}
            className={`ml-3 flex-shrink-0 ${config.iconColor} hover:opacity-75 transition-opacity`}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Notification;
